import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap'
import useFetch from '../../../_fetch'

type CampaignUrlAppend = {
  network: string
  id: string
  urls: string[]
  updatedAt?: string
}

type CampaignUrlResponse = {
  networks?: string[]
  items?: CampaignUrlAppend[]
}

function formatDateTime(value?: string) {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('da-DK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function urlsToTextarea(urls: string[]) {
  return urls.join('\n')
}

function textareaToUrls(value: string) {
  return value
    .split(/\r?\n/)
    .map((url) => url.trim())
    .filter(Boolean)
}

export default function PublisherCampaignUrls() {
  const { doFetch } = useFetch()

  const [items, setItems] = useState<CampaignUrlAppend[]>([])
  const [networks, setNetworks] = useState<string[]>([])
  const [network, setNetwork] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [urls, setUrls] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletingKey, setDeletingKey] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const networkCompare = a.network.localeCompare(b.network)
      if (networkCompare !== 0) return networkCompare

      return a.id.localeCompare(b.id)
    })
  }, [items])

  const load = async () => {
    setLoading(true)
    setError('')

    try {
      const res = await doFetch('/user/admin/publisher-campaign-urls')
      if (res.status >= 400) throw await res.text()

      const data: CampaignUrlResponse = await res.json()
      const nextNetworks = Array.isArray(data.networks)
        ? [...data.networks].sort((a, b) => a.localeCompare(b))
        : []

      setNetworks(nextNetworks)
      setItems(Array.isArray(data.items) ? data.items : [])
      setNetwork((current) => current || nextNetworks[0] || '')
    } catch (err: any) {
      setError(typeof err === 'string' ? err : 'Could not load campaign URLs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetForm = () => {
    setCampaignId('')
    setUrls('')
  }

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const res = await doFetch('/user/admin/publisher-campaign-urls', {
        network,
        id: campaignId,
        urls: textareaToUrls(urls),
      })
      if (res.status >= 400) throw await res.text()

      const saved: CampaignUrlAppend = await res.json()

      setItems((prev) => {
        const rest = prev.filter((item) => !(item.network === saved.network && item.id === saved.id))
        return [...rest, saved]
      })
      setNetwork(saved.network)
      setCampaignId(saved.id)
      setUrls(urlsToTextarea(saved.urls))
      setSuccess('Campaign URL appends saved.')
    } catch (err: any) {
      setError(typeof err === 'string' ? err : 'Could not save campaign URLs')
    } finally {
      setSaving(false)
    }
  }

  const edit = (item: CampaignUrlAppend) => {
    setNetwork(item.network)
    setCampaignId(item.id)
    setUrls(urlsToTextarea(item.urls))
    setError('')
    setSuccess('')
  }

  const remove = async (item: CampaignUrlAppend) => {
    const key = `${item.network}:${item.id}`
    setDeletingKey(key)
    setError('')
    setSuccess('')

    try {
      const res = await doFetch('/user/admin/publisher-campaign-urls', {
        network: item.network,
        id: item.id,
      }, 'DELETE')
      if (res.status >= 400) throw await res.text()

      setItems((prev) => prev.filter((curr) => !(curr.network === item.network && curr.id === item.id)))
      if (network === item.network && campaignId === item.id) resetForm()
      setSuccess('Campaign URL appends deleted.')
    } catch (err: any) {
      setError(typeof err === 'string' ? err : 'Could not delete campaign URLs')
    } finally {
      setDeletingKey('')
    }
  }

  return (
    <div className="pe-2">
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Campaigns</h5>
              <div className="text-muted small">Append extra shop URLs to publisher campaigns by network and campaign ID.</div>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={save}>
            <div className="d-flex flex-wrap gap-3">
              <Form.Group style={{ minWidth: 220, flex: '1 1 220px' }}>
                <Form.Label>Network</Form.Label>
                <Form.Select value={network} onChange={(event) => setNetwork(event.target.value)} required>
                  {networks.map((networkName) => (
                    <option key={networkName} value={networkName}>{networkName}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group style={{ minWidth: 220, flex: '1 1 220px' }}>
                <Form.Label>Campaign ID</Form.Label>
                <Form.Control
                  value={campaignId}
                  onChange={(event) => setCampaignId(event.target.value)}
                  placeholder="12345"
                  required
                />
                <div className="text-muted small mt-1">The campaign ID from the selected network.</div>
              </Form.Group>
            </div>

            <Form.Group className="mt-3">
              <Form.Label>URLs</Form.Label>
              <div className="text-muted small mb-2">Enter one URL per line.</div>
              <Form.Control
                as="textarea"
                rows={5}
                value={urls}
                onChange={(event) => setUrls(event.target.value)}
                placeholder="https://example.com"
                required
              />
            </Form.Group>

            <div className="d-flex gap-2 mt-3">
              <Button type="submit" disabled={saving || !network}>
                {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                Save
              </Button>
              <Button type="button" variant="outline-secondary" onClick={resetForm}>
                Clear
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h5 className="mb-3">Appended URLs</h5>

          {loading ? (
            <Spinner animation="border" />
          ) : sortedItems.length ? (
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Network</th>
                    <th>Campaign ID</th>
                    <th>URLs</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => {
                    const key = `${item.network}:${item.id}`

                    return (
                      <tr key={key}>
                        <td>{item.network}</td>
                        <td>{item.id}</td>
                        <td style={{ minWidth: 280 }}>
                          {item.urls.map((url) => (
                            <div key={url} className="text-break">{url}</div>
                          ))}
                        </td>
                        <td>{formatDateTime(item.updatedAt)}</td>
                        <td className="text-end">
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => edit(item)}>
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => remove(item)}
                            disabled={deletingKey === key}
                          >
                            {deletingKey === key ? <Spinner animation="border" size="sm" /> : 'Delete'}
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-muted small">No campaign URL appends have been added.</div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
